/**
 * A very small promise wrapper over IndexedDB: open the database, run one
 * request in a transaction, read a whole store. Kept here instead of a
 * dependency because the device store needs only these few operations.
 */

export type StoreName =
  'kv' | 'pins' | 'customers' | 'pendingSales' | 'delivered' | 'outbox' | 'recent' | 'tombstones';

export const DATABASE_NAME = 'fuel-pda-offline';
/** 2 adds `tombstones` (sales no longer deliverable). */
export const DATABASE_VERSION = 2;

/** Creates the stores of fuel-pda.md section 13.3 on the first open. */
function upgrade(db: IDBDatabase): void {
  const existing = new Set(Array.from(db.objectStoreNames));
  if (!existing.has('kv')) db.createObjectStore('kv');
  if (!existing.has('pins')) db.createObjectStore('pins', { keyPath: 'userId' });
  if (!existing.has('customers')) {
    const customers = db.createObjectStore('customers', { keyPath: 'id' });
    customers.createIndex('phone', 'phone', { unique: false });
  }
  if (!existing.has('pendingSales')) db.createObjectStore('pendingSales', { keyPath: 'reference' });
  if (!existing.has('delivered')) db.createObjectStore('delivered', { keyPath: 'reference' });
  if (!existing.has('outbox')) {
    const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
    outbox.createIndex('seq', 'seq', { unique: true });
  }
  if (!existing.has('recent')) {
    const recent = db.createObjectStore('recent', { keyPath: 'id' });
    recent.createIndex('reference', 'reference', { unique: false });
  }
  if (!existing.has('tombstones')) db.createObjectStore('tombstones', { keyPath: 'reference' });
}

export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => resolve(request.result));
    request.addEventListener('error', () =>
      reject(request.error ?? new Error('IndexedDB request failed')),
    );
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.addEventListener('complete', () => resolve());
    tx.addEventListener('abort', () =>
      reject(tx.error ?? new Error('IndexedDB transaction aborted')),
    );
    tx.addEventListener('error', () =>
      reject(tx.error ?? new Error('IndexedDB transaction failed')),
    );
  });
}

export function openDatabase(
  factory: IDBFactory,
  name: string = DATABASE_NAME,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(name, DATABASE_VERSION);
    request.addEventListener('upgradeneeded', () => upgrade(request.result));
    request.addEventListener('success', () => resolve(request.result));
    request.addEventListener('error', () =>
      reject(request.error ?? new Error('IndexedDB open failed')),
    );
    request.addEventListener('blocked', () => reject(new Error('IndexedDB open blocked')));
  });
}

/**
 * Runs `work` in one transaction over `stores` and resolves with its result
 * once the transaction has committed, so a write is durable when it resolves.
 */
export async function inTransaction<T>(
  db: IDBDatabase,
  stores: StoreName | StoreName[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const tx = db.transaction(stores, mode);
  const done = transactionDone(tx);
  try {
    const result = await work(tx);
    await done;
    return result;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // Already finished or aborted.
    }
    await done.catch(() => undefined);
    throw error;
  }
}
